import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload as UploadIcon, FileSpreadsheet, Image as ImageIcon, Save, LayoutGrid, Rows3 } from "lucide-react";
import { Users, FileText, Clock, CheckCircle2, XCircle, Plus, Trash2, Pencil, Crown, ScrollText, TrendingUp, Copy, RefreshCw, KeyRound, Mail, Check, X, ShieldCheck } from "lucide-react";
import PortalNav from "../components/PortalNav";
import CreateClientModal from "../components/CreateClientModal";
import EditClientModal from "../components/EditClientModal";
import ImportClientsModal from "../components/ImportClientsModal";
import UpgradeModal from "../components/UpgradeModal";
import { StatusBadge, EmptyState, isOverdue, OverdueBadge } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
    <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" strokeWidth={1.8} /></div>
    <div className="text-2xl font-bold text-[#1a1a2e]">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dash, setDash] = useState(null);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invites, setInvites] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [showClient, setShowClient] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [branding, setBranding] = useState({ logo_url: "", reply_to: "", weekly_digest: false });
  const [savingBrand, setSavingBrand] = useState(false);
  const [statView, setStatView] = useState(() => localStorage.getItem("for_statview") || "boxes");

  useEffect(() => { localStorage.setItem("for_statview", statView); }, [statView]);

  const load = async () => {
    try {
      const [d, c, r, a, inv, ap] = await Promise.all([
        api.get("/admin/dashboard"), api.get("/clients"), api.get("/requests"), api.get("/audit"), api.get("/invites"), api.get("/approvals"),
      ]);
      setDash(d.data); setClients(c.data.clients); setRequests(r.data.requests); setAudit(a.data.audit);
      setApprovals(ap.data.approvals || []);
      const seen = {};
      for (const iv of (inv.data.invites || [])) {
        const cur = seen[iv.email];
        if (!cur || (iv.client_name && !cur.client_name) || (iv.created_at || "") > (cur.created_at || "")) seen[iv.email] = iv;
      }
      setInvites(Object.values(seen));
    } catch (e) { /* handled by interceptor */ }
  };
  useEffect(() => { load(); loadBranding(); }, []);

  const loadBranding = async () => {
    try { const { data } = await api.get("/firm/settings"); setBranding({ logo_url: data.logo_url || "", reply_to: data.reply_to || "", weekly_digest: !!data.weekly_digest }); }
    catch (e) { /* ignore */ }
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try { await api.put("/firm/settings", branding); toast({ title: "Settings saved", description: "Invitation emails and digest preference updated." }); }
    catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSavingBrand(false); }
  };

  const sendDigestNow = async () => {
    try { await api.post("/admin/send-digest-now"); toast({ title: "Digest sent", description: "A sample weekly digest was emailed to you." }); }
    catch (e) { toast({ title: "Could not send digest", variant: "destructive" }); }
  };

  const delClient = async (id) => {
    try { await api.delete(`/clients/${id}`); toast({ title: "Client deleted" }); load(); }
    catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const actApproval = async (id, decision) => {
    try {
      await api.post(`/approvals/${id}/${decision}`);
      toast({ title: decision === "approve" ? "Request approved" : "Request rejected", description: "The requester has been notified by email." });
      load();
    } catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };
  const pendingApprovals = approvals.filter((a) => a.status === "pending");

  const copyInvite = (inv) => {
    navigator.clipboard.writeText(`Email: ${inv.email}\nPassword: ${inv.temp_password}\nRole: ${inv.role}`);
    toast({ title: "Copied credentials" });
  };

  const resetTemp = async (inv) => {
    try {
      const { data } = await api.post(`/invites/resend`, { email: inv.email });
      toast({ title: "Invite resent", description: `A fresh sign-in link was emailed to ${data.email}` });
      load();
    } catch (e) { toast({ title: "Reset failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };

  const sub = dash?.subscription;
  const isFree = sub?.plan === "free";
  const stats = [
    { icon: Users, label: "Clients", value: dash?.clients ?? 0, tint: "bg-indigo-50 text-indigo-700" },
    { icon: FileText, label: "Total requests", value: dash?.total_requests ?? 0, tint: "bg-blue-50 text-blue-700" },
    { icon: Clock, label: "Open", value: (dash?.total_requests ?? 0) - (dash?.by_status?.["Accepted & Closed"] ?? 0), tint: "bg-amber-50 text-amber-700" },
    { icon: CheckCircle2, label: "Accepted & Closed", value: dash?.by_status?.["Accepted & Closed"] ?? 0, tint: "bg-emerald-50 text-emerald-700" },
  ];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <PortalNav title="Admin" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a2e]">Firm overview</h1>
            <p className="mt-1 text-slate-500">Clients, requests and compliance activity at a glance.</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowClient(true)} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Plus className="mr-2 h-4 w-4" /> Add client</Button>
            <Button onClick={() => setShowImport(true)} variant="outline" className="rounded-xl border-slate-300"><FileSpreadsheet className="mr-2 h-4 w-4" /> Import CSV</Button>
          </div>
        </div>

        {/* Subscription banner */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50"><Crown className="h-6 w-6 text-indigo-700" /></div>
            <div>
              <div className="font-semibold text-[#1a1a2e]">{isFree ? "Starter (Free) plan" : `${sub?.plan === "annual" ? "Annual" : "Monthly"} plan · active`}</div>
              <div className="text-sm text-slate-500">
                {isFree
                  ? `${dash?.clients ?? 0}/${dash?.free_client_limit ?? 5} clients · up to ${dash?.free_requests_per_client ?? 10} requests per client`
                  : "Unlimited clients & requests"}
              </div>
            </div>
          </div>
          {isFree && <Button onClick={() => setShowUpgrade(true)} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]">Upgrade</Button>}
        </div>

        {/* Stats with view toggle */}
        <div className="mt-8 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Overview</h2>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setStatView("boxes")} title="Boxes view" className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${statView === "boxes" ? "bg-[#2f2b8f] text-white" : "text-slate-500 hover:text-slate-700"}`}><LayoutGrid className="h-3.5 w-3.5" /> Boxes</button>
            <button onClick={() => setStatView("tiles")} title="Tiles view" className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${statView === "tiles" ? "bg-[#2f2b8f] text-white" : "text-slate-500 hover:text-slate-700"}`}><Rows3 className="h-3.5 w-3.5" /> Tiles</button>
          </div>
        </div>

        {statView === "boxes" ? (
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} tint={s.tint} />)}
          </div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
            {stats.map((s) => (
              <div key={s.label} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${s.tint}`}><s.icon className="h-4.5 w-4.5" strokeWidth={1.8} /></div>
                  <span className="text-sm font-medium text-slate-600">{s.label}</span>
                </div>
                <span className="text-xl font-bold text-[#1a1a2e]">{s.value}</span>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="clients" className="mt-10">
          <TabsList className="rounded-xl bg-slate-100 p-1">
            <TabsTrigger value="clients" className="rounded-lg">Clients</TabsTrigger>
            <TabsTrigger value="requests" className="rounded-lg">Requests</TabsTrigger>
            <TabsTrigger value="approvals" className="rounded-lg" data-testid="approvals-tab">
              Approvals
              {pendingApprovals.length > 0 && <span data-testid="approvals-count" className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{pendingApprovals.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="credentials" className="rounded-lg">Credentials</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-lg">Audit trail</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">Settings</TabsTrigger>
          </TabsList>

          {/* Clients */}
          <TabsContent value="clients" className="mt-5">
            {clients.length === 0 ? (
              <EmptyState title="No clients yet" subtitle="Add your first client and invite the handling employee and client contact."
                action={<Button onClick={() => setShowClient(true)} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Plus className="mr-2 h-4 w-4" /> Add client</Button>} />
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-6 py-3 font-semibold">Client</th><th className="px-6 py-3 font-semibold">Engagement</th>
                      <th className="px-6 py-3 font-semibold">Period</th><th className="px-6 py-3 font-semibold">Handling</th>
                      <th className="px-6 py-3 font-semibold">Created</th><th className="px-6 py-3 font-semibold">Requests</th>
                      <th className="px-6 py-3 font-semibold text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {clients.map((c) => (
                        <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-6 py-4 font-semibold text-[#1a1a2e]">{c.name}</td>
                          <td className="px-6 py-4 text-slate-600">{c.nature_of_engagement}</td>
                          <td className="px-6 py-4 text-slate-600">{c.period}</td>
                          <td className="px-6 py-4 text-slate-600">{c.employee_name}</td>
                          <td className="px-6 py-4 text-slate-500">{(c.created_at || "").slice(0, 10)}</td>
                          <td className="px-6 py-4"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{c.request_count}</span></td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button data-testid={`edit-client-${c.id}`} onClick={() => setEditClient(c)} title="Edit client" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-[#2f2b8f]"><Pencil className="h-4 w-4" /></button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-2xl">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                                    <AlertDialogDescription>This removes the client and all its requests. This action cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => delClient(c.id)} className="rounded-xl bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Requests */}
          <TabsContent value="requests" className="mt-5">
            {requests.length === 0 ? (
              <EmptyState title="No requests yet" subtitle="Create a document request to get started." />
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-6 py-3 font-semibold">Request</th><th className="px-6 py-3 font-semibold">Client</th>
                      <th className="px-6 py-3 font-semibold">Assignee</th><th className="px-6 py-3 font-semibold">Deadline</th>
                      <th className="px-6 py-3 font-semibold">Status</th>
                    </tr></thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id} onClick={() => navigate(`/request/${r.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-6 py-4"><div className="font-semibold text-[#1a1a2e]">{r.seq != null && <span data-testid={`req-seq-${r.id}`} className="mr-1.5 font-mono text-xs font-semibold text-slate-400">#{r.seq}</span>}{r.title}</div><div className="text-xs text-slate-400">{r.tasks?.length || 0} tasks</div></td>
                          <td className="px-6 py-4 text-slate-600">{r.client_name}</td>
                          <td className="px-6 py-4 text-slate-600">{(r.assignee_names && r.assignee_names.length ? r.assignee_names.join(", ") : (r.assignee_emails && r.assignee_emails.length ? r.assignee_emails.join(", ") : r.assignee_name))}</td>
                          <td className="px-6 py-4 text-slate-600">{r.deadline || "—"}</td>
                          <td className="px-6 py-4"><div className="flex items-center gap-2"><StatusBadge status={r.status} />{isOverdue(r) && <OverdueBadge />}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Approvals */}
          <TabsContent value="approvals" className="mt-5">
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>When a firm member requests to change access — their own or another member's — or to add/remove a member, it lands here for your approval. Nothing changes until you approve.</span>
            </div>
            {approvals.length === 0 ? (
              <EmptyState title="No approval requests" subtitle="Access-change requests from your firm members will appear here." />
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-6 py-3 font-semibold">Requested by</th><th className="px-6 py-3 font-semibold">Client</th>
                      <th className="px-6 py-3 font-semibold">Change requested</th><th className="px-6 py-3 font-semibold">When</th>
                      <th className="px-6 py-3 font-semibold text-right">Status / Action</th>
                    </tr></thead>
                    <tbody>
                      {approvals.map((a) => (
                        <tr key={a.id} data-testid={`approval-row-${a.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-6 py-4"><div className="font-semibold text-[#1a1a2e]">{a.requested_by_name}</div><div className="text-xs text-slate-400">{a.requested_by_email}</div></td>
                          <td className="px-6 py-4 text-slate-600">{a.client_name}</td>
                          <td className="px-6 py-4 text-slate-600">{a.action_desc}</td>
                          <td className="px-6 py-4 text-slate-500">{(a.created_at || "").replace("T", " ").slice(0, 16)}</td>
                          <td className="px-6 py-4 text-right">
                            {a.status === "pending" ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button data-testid={`approve-btn-${a.id}`} onClick={() => actApproval(a.id, "approve")} size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                                <Button data-testid={`reject-btn-${a.id}`} onClick={() => actApproval(a.id, "reject")} size="sm" variant="outline" className="rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                              </div>
                            ) : (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                {a.status === "approved" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {a.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Credentials & Invites */}
          <TabsContent value="credentials" className="mt-5">
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800">
              <KeyRound className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Invitees sign in <strong>passwordlessly</strong> — they enter their email and receive a one-time code, then can optionally set a password. No passwords are shared. Use "Resend invite" to email the sign-in instructions again.</span>
            </div>
            {invites.length === 0 ? (
              <EmptyState title="No invites yet" subtitle="Add a client to invite the handling employee and client contact." />
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-6 py-3 font-semibold">User</th><th className="px-6 py-3 font-semibold">Role</th>
                      <th className="px-6 py-3 font-semibold">Client</th><th className="px-6 py-3 font-semibold">Sign-in</th>
                      <th className="px-6 py-3 font-semibold">Invited</th><th className="px-6 py-3 font-semibold text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {invites.map((inv) => (
                        <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-6 py-4"><div className="inline-flex items-center gap-2 text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {inv.email}</div></td>
                          <td className="px-6 py-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${inv.role === "employee" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>{inv.role}</span></td>
                          <td className="px-6 py-4 text-slate-600">{inv.client_name || "—"}</td>
                          <td className="px-6 py-4">
                            {inv.password_changed
                              ? <span className="text-xs font-medium text-slate-500">Password set by user</span>
                              : <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700"><KeyRound className="h-3 w-3" /> Email code (passwordless)</span>}
                          </td>
                          <td className="px-6 py-4 text-slate-500">{(inv.created_at || "").slice(0, 10)}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => resetTemp(inv)} title="Resend sign-in invite" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-amber-600"><RefreshCw className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Audit */}
          <TabsContent value="audit" className="mt-5">
            <div className="rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
              {audit.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No activity yet.</div> : (
                <ul className="divide-y divide-slate-50">
                  {audit.map((e) => (
                    <li key={e.id} className="flex items-start gap-3 px-4 py-3">
                      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50"><ScrollText className="h-4 w-4 text-slate-400" /></div>
                      <div className="flex-1">
                        <div className="text-sm text-[#1a1a2e]"><span className="font-semibold">{e.actor}</span> · <span className="text-slate-500">{e.action.replace(/_/g, " ")}</span></div>
                        {e.detail && <div className="text-xs text-slate-500">{e.detail}</div>}
                      </div>
                      <div className="text-xs text-slate-400">{(e.timestamp || "").replace("T", " ").slice(0, 16)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* Settings - Email branding */}
          <TabsContent value="settings" className="mt-5">
            <div className="max-w-2xl rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><ImageIcon className="h-4 w-4" /> Email branding</h2>
              <p className="mt-1 text-sm text-slate-500">Personalise the invitation and reminder emails your clients and employees receive.</p>
              <div className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label>Firm logo URL</Label>
                  <Input value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} placeholder="https://yourfirm.com/logo.png" className="rounded-xl" />
                  <p className="text-xs text-slate-400">Paste a public image URL. Shown in the header of every email instead of the default logo.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Reply-to email</Label>
                  <Input type="email" value={branding.reply_to} onChange={(e) => setBranding({ ...branding, reply_to: e.target.value })} placeholder="partners@yourfirm.com" className="rounded-xl" />
                  <p className="text-xs text-slate-400">When recipients reply to an invitation email, it goes to this address.</p>
                </div>
                {branding.logo_url && (
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Preview</p>
                    <img src={branding.logo_url} alt="logo preview" className="max-h-10 max-w-[180px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                )}
                <Button onClick={saveBranding} disabled={savingBrand} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Save className="mr-2 h-4 w-4" /> {savingBrand ? "Saving..." : "Save settings"}</Button>
              </div>
            </div>

            <div className="mt-6 max-w-2xl rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><Mail className="h-4 w-4" /> Weekly activity digest</h2>
              <p className="mt-1 text-sm text-slate-500">Get a weekly email summarising open, submitted and overdue requests across all your clients.</p>
              <div className="mt-5 flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-[#1a1a2e]">Email me a weekly digest</div>
                  <div className="text-xs text-slate-500">Sent to all admins of your firm. Turn off anytime.</div>
                </div>
                <Switch data-testid="weekly-digest-switch" checked={branding.weekly_digest} onCheckedChange={(v) => setBranding({ ...branding, weekly_digest: v })} />
              </div>
              <div className="mt-4 flex gap-3">
                <Button onClick={saveBranding} disabled={savingBrand} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Save className="mr-2 h-4 w-4" /> Save preference</Button>
                <Button variant="outline" onClick={sendDigestNow} className="rounded-xl border-slate-300"><Mail className="mr-2 h-4 w-4" /> Send me a sample now</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <CreateClientModal open={showClient} onClose={() => setShowClient(false)} onCreated={load} />
      <EditClientModal open={!!editClient} client={editClient} onClose={() => setEditClient(null)} onUpdated={load} />
      <ImportClientsModal open={showImport} onClose={() => setShowImport(false)} onImported={load} />
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} onSuccess={load} />
    </div>
  );
};

export default AdminDashboard;
